import { useQuery } from '@tanstack/react-query'
import type { PluginExtensionType } from '@orbind/core'
import { api } from '../../lib/api.ts'

/**
 * As extensões de plugin instaladas NESTE workspace
 * (SPEC-20260926-2036-plugin-tela-no-rail e irmãs).
 *
 * "Declarada no manifesto" e "disponível aqui" são coisas diferentes: o
 * servidor já filtra por instalação viva, plugin não arquivado e status ativo.
 * O cliente só desenha o que vier.
 */

export type ExtensaoInstalada = {
  installationId: string
  pluginId: string
  pluginKey: string
  pluginName: Record<string, string>
  tipo: string
  chave: string
  config: {
    label?: Record<string, string>
    icon?: string
    url?: string
    endpoint?: string
    ttlSeconds?: number
    events?: string[]
  }
}

/** Rótulo na locale da pessoa, com queda para a chave da extensão. */
export function rotuloDaExtensao(e: ExtensaoInstalada, locale = 'pt-BR'): string {
  return e.config.label?.[locale] ?? Object.values(e.config.label ?? {})[0] ?? e.chave
}

/**
 * Um id estável para a extensão dentro do shell.
 *
 * Prefixo `plugin:` porque ele vira id de módulo do rail e chave de permissão
 * — e não pode colidir com módulo nativo nem hoje nem quando alguém criar um
 * módulo novo.
 */
export const idDaExtensao = (e: { pluginKey: string; chave: string }) => `plugin:${e.pluginKey}:${e.chave}`

/** A rota da tela de um plugin. Sempre sob `/plugins/`. */
export const rotaDaExtensao = (e: { pluginKey: string; chave: string }) =>
  `/plugins/${e.pluginKey}/${e.chave}`

export function useExtensoesInstaladas(tipo: PluginExtensionType, ligado = true) {
  return useQuery({
    queryKey: ['plugins', 'extensions', tipo],
    enabled: ligado,
    // muda só quando alguém instala ou desinstala — não vale repetir a cada tela
    staleTime: 5 * 60 * 1000,
    queryFn: async () =>
      (
        (await (await api.plugins.extensions.$get({ query: { type: tipo } })).json()) as {
          extensoes: ExtensaoInstalada[]
        }
      ).extensoes,
  })
}
